// src/components/AuthPanel/LinkAccountModal.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Gösterilme koşulu: useAuth().linkRequired === true
// Kullanıcı aynı email ile hem email/şifre hem Google ile giriş denedi.
// Şifresiyle doğrulayıp hesapları birleştiririz.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const C = {
  bg:        "#12100a",
  border:    "rgba(212,168,83,0.25)",
  gold:      "#d4a853",
  goldDim:   "rgba(212,168,83,0.5)",
  goldGhost: "rgba(212,168,83,0.18)",
  ivory:     "#e8d5b0",
  ivoryDim:  "rgba(232,213,176,0.65)",
  red:       "#e05c5c",
  green:     "#6ec97a",
};

export default function LinkAccountModal() {
  const { t }                                    = useTranslation();
  const { linkData, linkGoogleAccount, cancelLink } = useAuth();

  const [password, setPassword] = useState("");
  const [show,     setShow]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  async function handleLink() {
    if (!password) { setError(t('auth.errors.passwordRequired')); return; }
    setLoading(true); setError("");
    try {
      await linkGoogleAccount(password);
      // Başarılı — modal kapanır, onAuthStateChanged tetiklenir
    } catch (e) {
      if (e.code === "auth/wrong-password" || e.code === "auth/invalid-credential") {
        setError(t('auth.errors.wrongPassword'));
      } else {
        setError(t('auth.errors.generic'));
      }
      setLoading(false);
    }
  }

  const INP = {
    display: "block", width: "100%", boxSizing: "border-box",
    padding: "12px 14px 12px 14px",
    background: "#1a1208", border: `1px solid rgba(212,168,83,0.2)`,
    color: C.ivory, fontFamily: "'Courier Prime', monospace",
    fontSize: 13, outline: "none", letterSpacing: "0.04em",
    paddingRight: 44,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`,
        padding: "40px 36px", width: "100%", maxWidth: 420,
        boxShadow: "0 0 80px rgba(0,0,0,0.7)",
      }}>
        {/* Başlık */}
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, fontStyle: "italic", color: C.gold, marginBottom: 6 }}>
          BoneCast
        </div>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, letterSpacing: "0.32em", color: C.goldGhost, textTransform: "uppercase", marginBottom: 24 }}>
          {t('auth.link.eyebrow')}
        </div>

        {/* Bilgi kutusu */}
        <div style={{
          padding: "14px 16px", marginBottom: 24,
          background: "rgba(212,168,83,0.05)",
          border: `1px solid rgba(212,168,83,0.15)`,
          borderLeft: `3px solid ${C.gold}`,
        }}>
          <p style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, color: C.ivoryDim, lineHeight: 1.75, margin: 0 }}>
            {t('auth.link.desc')}
          </p>
          <p style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: C.goldDim, marginTop: 8, margin: "8px 0 0", letterSpacing: "0.04em" }}>
            📧 {linkData?.email}
          </p>
        </div>

        {/* Şifre input */}
        <label style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, letterSpacing: "0.26em", color: C.goldDim, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
          {t('auth.link.passwordLabel')}
        </label>
        <div style={{ position: "relative", marginBottom: error ? 8 : 20 }}>
          <input
            style={INP}
            type={show ? "text" : "password"}
            placeholder={t('auth.link.passwordPlaceholder')}
            value={password}
            onChange={e => { setPassword(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLink()}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            style={{
              position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(212,168,83,0.4)", fontSize: 13,
            }}
          >
            {show ? "👁" : "🙈"}
          </button>
        </div>

        {error && (
          <p style={{
            fontFamily: "'Courier Prime', monospace", fontSize: 11, color: C.red,
            marginBottom: 16, padding: "8px 12px",
            border: "1px solid rgba(224,92,92,0.2)", background: "rgba(224,92,92,0.05)",
          }}>
            ⚠ {error}
          </p>
        )}

        {/* Butonlar */}
        <button
          onClick={handleLink}
          disabled={loading}
          style={{
            width: "100%", padding: "13px", marginBottom: 10,
            background: loading ? "rgba(212,168,83,0.22)" : C.gold,
            border: "none",
            color: loading ? "rgba(13,10,5,0.35)" : "#0d0a05",
            fontFamily: "'Courier Prime', monospace", fontWeight: "bold",
            fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
          }}
        >
          {loading ? t('common.sending') : t('auth.link.btn')}
        </button>

        <button
          onClick={cancelLink}
          style={{
            width: "100%", padding: "10px",
            background: "transparent",
            border: `1px solid rgba(212,168,83,0.15)`,
            color: "rgba(212,168,83,0.4)",
            fontFamily: "'Courier Prime', monospace", fontSize: 11,
            letterSpacing: "0.18em", textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.35)"; e.currentTarget.style.color = "rgba(212,168,83,0.65)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.15)"; e.currentTarget.style.color = "rgba(212,168,83,0.4)"; }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
